import { useEffect, useMemo, useState } from "react";
import { hkInvAPI } from "../hkInvApi";
import { toast } from "sonner";
import { Layers, Plus, AlertTriangle, ArrowUpCircle, MinusCircle, CheckCircle2 } from "lucide-react";
import { cls, Fld, Modal, Footer } from "../../kitchen/KitchenPantry";
import { NumberInput } from "@/components/ui/number-input";

export default function HKFloorPars() {
  const [floors, setFloors] = useState([]);
  const [items, setItems] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [consume, setConsume] = useState(null);

  const load = () => hkInvAPI.floors().then(setFloors).catch(()=>{});
  useEffect(() => { load(); hkInvAPI.items().then(setItems).catch(()=>{}); }, []);

  const shortFloors = useMemo(() => floors.filter(f => f.short_count > 0).length, [floors]);

  const restock = async (floor, item_id) => {
    try { await hkInvAPI.restockFloor({ floor, item_id }); toast.success(`Floor ${floor} restocked to par`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const pct = (on, par) => { const p = Number(par||0); return p > 0 ? Math.min(100, Math.round((Number(on||0)/p)*100)) : 0; };

  return (
    <div className="space-y-4" data-testid="hkinv-floors">
      {shortFloors > 0 && (
        <div className="flex items-center gap-2 rounded-xl bg-rose-50 border border-rose-200 px-4 py-2.5 text-[13px] text-rose-800" data-testid="hkinv-floors-short-banner">
          <AlertTriangle className="w-4 h-4 shrink-0"/>
          <span><b>{shortFloors}</b> floor{shortFloors>1?"s are":" is"} running short of par — restock before guests notice.</span>
        </div>
      )}
      <div className="flex items-center gap-3">
        <h3 className="text-[14px] font-bold uppercase tracking-wider text-slate-500">Floor Status Board</h3>
        <button onClick={()=>setAddOpen(true)} data-testid="hkinv-floor-add-par"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-semibold inline-flex items-center gap-2 hover:brightness-95">
          <Plus className="w-4 h-4"/> Set Floor Par
        </button>
      </div>

      {floors.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <Layers className="w-12 h-12 mx-auto text-slate-300 mb-2"/>
          <div className="text-[13px] text-slate-500">No floor pars set yet. Click <b>Set Floor Par</b> to define expected stock per floor.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {floors.map(f => (
            <div key={f.floor} data-testid={`hkinv-floor-card-${f.floor}`}
                 className={`bg-white border rounded-2xl overflow-hidden ${f.short_count > 0 ? "border-rose-200" : "border-slate-200"}`}>
              <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-hocp-navy/10 flex items-center justify-center text-hocp-navy font-bold text-[13px]">{f.floor}</div>
                  <span className="font-display font-semibold text-hocp-navy">Floor {f.floor}</span>
                </div>
                {f.short_count > 0
                  ? <span className="px-2 py-0.5 rounded-md bg-rose-100 text-rose-700 text-[11px] font-medium inline-flex items-center gap-1" data-testid={`hkinv-floor-short-${f.floor}`}><AlertTriangle className="w-3 h-3"/>{f.short_count} short</span>
                  : <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[11px] font-medium inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/>At par</span>}
              </div>
              <div className="divide-y divide-slate-100">
                {f.items.map(it => (
                  <div key={it.id} className="px-4 py-2.5" data-testid={`hkinv-floor-item-${f.floor}-${it.item_id}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[13px] font-medium text-hocp-navy">{it.item_name}</span>
                      <span className={`text-[12px] mono tabular-nums font-semibold ${it.short ? "text-rose-600" : "text-slate-600"}`}>
                        {it.on_hand} / {it.par_level} {it.unit}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full rounded-full ${it.short ? "bg-rose-400" : "bg-emerald-400"}`} style={{ width: `${pct(it.on_hand, it.par_level)}%` }}/>
                      </div>
                      <button onClick={()=>restock(f.floor, it.item_id)} disabled={!it.short}
                              data-testid={`hkinv-floor-restock-${f.floor}-${it.item_id}`}
                              className="h-7 px-2 rounded-md text-[11.5px] font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-30 disabled:hover:bg-white">
                        <ArrowUpCircle className="w-3.5 h-3.5"/> Restock
                      </button>
                      <button onClick={()=>setConsume({ floor: f.floor, item: it })}
                              data-testid={`hkinv-floor-consume-${f.floor}-${it.item_id}`}
                              className="h-7 px-2 rounded-md text-[11.5px] font-medium border border-slate-200 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1">
                        <MinusCircle className="w-3.5 h-3.5"/> Use
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {addOpen && <SetParDialog items={items} onClose={()=>setAddOpen(false)} onDone={()=>{ setAddOpen(false); load(); }}/>}
      {consume && <ConsumeDialog data={consume} onClose={()=>setConsume(null)} onDone={()=>{ setConsume(null); load(); }}/>}
    </div>
  );
}

function SetParDialog({ items, onClose, onDone }) {
  const [f, setF] = useState({ floor: "", item_id: "", par_level: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.floor.trim()) { toast.error("Floor is required"); return; }
    if (!f.item_id) { toast.error("Pick an item"); return; }
    if (!(Number(f.par_level) > 0)) { toast.error("Par level must be greater than 0"); return; }
    setSaving(true);
    try { await hkInvAPI.setFloorPar({ floor: f.floor.trim(), item_id: f.item_id, par_level: f.par_level });
          toast.success("Floor par saved"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Set Floor Par" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Floor"><input value={f.floor} onChange={e=>setF({...f, floor: e.target.value})} placeholder="e.g. 3" data-testid="hkinv-floor-par-floor" className={cls}/></Fld>
        <Fld label="Par level (target)"><NumberInput step="0.001" value={f.par_level} onChange={e=>setF({...f, par_level: e.target.value})} placeholder="e.g. 20" data-testid="hkinv-floor-par-level" className={cls}/></Fld>
        <Fld label="Item" full>
          <select value={f.item_id} onChange={e=>setF({...f, item_id: e.target.value})} data-testid="hkinv-floor-par-item" className={cls}>
            <option value="">Select item…</option>
            {items.map(x => <option key={x.id} value={x.id}>{x.item_name} — {x.current_stock} {x.unit} in store</option>)}
          </select>
        </Fld>
      </div>
      <Footer onClose={onClose} onSubmit={submit} saving={saving} testid="hkinv-floor-par-save"/>
    </Modal>
  );
}

function ConsumeDialog({ data, onClose, onDone }) {
  const [qty, setQty] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!(Number(qty) > 0)) { toast.error("Quantity must be greater than 0"); return; }
    setSaving(true);
    try { await hkInvAPI.consumeFloor({ floor: data.floor, item_id: data.item.item_id, qty });
          toast.success(`Used ${qty} on floor ${data.floor}`); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={`Use · ${data.item.item_name} · Floor ${data.floor}`} onClose={onClose}>
      <div className="text-[12.5px] text-slate-500 mb-2">On floor now: <b className="mono">{data.item.on_hand} {data.item.unit}</b> (par {data.item.par_level})</div>
      <Fld label="Quantity used on this floor" full>
        <NumberInput step="0.001" value={qty} onChange={e=>setQty(e.target.value)} placeholder="e.g. 5" data-testid="hkinv-floor-consume-qty" className={cls} autoFocus/>
      </Fld>
      <Footer onClose={onClose} onSubmit={submit} saving={saving} testid="hkinv-floor-consume-save"/>
    </Modal>
  );
}
